import DownloadIcon from "@assets/images/down-arrow.svg";
import { Button, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import ListIcon from "@material-ui/icons/List";
import Checkbox from "@saleor/components/Checkbox";
import Money from "@saleor/components/Money";
import Percent from "@saleor/components/Percent";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { BulkVoucherTabList_voucherGroups_edges_node } from "@saleor/discounts/types/BulkVoucherTabList";
import { VoucherListUrlSortField } from "@saleor/discounts/urls";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import {
  DiscountValueTypeEnum,
  VoucherDiscountType,
  VoucherTypeEnum
} from "@saleor/types/globalTypes";
import { getArrowDirection } from "@saleor/utils/sort";
import { getFooterColSpanWithBulkActions } from "@saleor/utils/tables";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

export interface VoucherListProps
  extends ListProps,
    ListActions,
    SortPage<VoucherListUrlSortField> {
  defaultCurrency: string;
  voucherGroups: BulkVoucherTabList_voucherGroups_edges_node[];
  onArrowClick: (id?: string) => () => void;
  onBulkGroupListDownload: (id?: string) => void;
  activeTab: string;
}

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("xl")]: {
      colEnd: {
        width: 180
      },
      colIcons: {
        width: 60
      },
      colMinSpent: {
        width: 180
      },
      colName: {},
      colStart: {
        width: 200
      },
      colUses: {
        width: 100
      },
      colValue: {
        width: 180
      }
    },
    btnList: {
      color: "#247CFF",
      cursor: "pointer"
    },
    colEnd: {
      textAlign: "right"
    },
    colMinSpent: {
      textAlign: "right"
    },
    colName: {
      overflow: "hidden",
      paddingLeft: 0,
      textOverflow: "ellipsis",
      width: 300
    },
    colStart: {
      textAlign: "right"
    },
    colType: {
      textAlign: "left",
      width: 200
    },
    colUses: {
      textAlign: "center"
    },
    colValue: {
      textAlign: "right"
    },
    emptyHeader: {
      textAlign: "left",
      width: 20
    },
    list: {
      color: "#247CFF"
    },
    tableCode: {
      backgroundColor: "#96a7f7",
      borderRadius: "5px",
      color: "#1440ff",
      fontWeight: "bold",
      padding: "6px",
      textAlign: "center",
      width: "auto !important"
    },
    tableCodes: {
      backgroundColor: "#fef7b4",
      borderRadius: "5px",
      color: "#edad32",
      fontWeight: "bold",
      overflow: "hidden",
      padding: "6px",
      textAlign: "center",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    tableRow: {
      cursor: "pointer"
    },
    textRight: {
      textAlign: "right"
    }
  }),
  { name: "VoucherList" }
);

const numberOfColumns = 9;
const tableHeadNumCol = 10;

const VoucherGroupList: React.FC<VoucherListProps> = props => {
  const {
    settings,
    defaultCurrency,
    disabled,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onBulkGroupListDownload,
    onSort,
    pageInfo,
    voucherGroups,
    isChecked,
    selected,
    sort,
    toggle,
    toggleAll,
    toolbar,
    onArrowClick
  } = props;

  const classes = useStyles(props);

  function getVoucherType(data) {
    let value = "";
    const type = data?.vouchers?.edges?.[0]?.node?.maxDiscountValue
      ? VoucherDiscountType.PERCENTAGE_AND_CAPPED
      : data?.vouchers?.edges?.[0]?.node?.type === VoucherTypeEnum.SHIPPING
      ? VoucherTypeEnum.SHIPPING
      : data?.vouchers?.edges?.[0]?.node?.type === VoucherTypeEnum.E_VOUCHER
      ? VoucherTypeEnum.E_VOUCHER
      : data?.vouchers?.edges?.[0]?.node?.discountValueType;

    switch (type) {
      case VoucherDiscountType.PERCENTAGE_AND_CAPPED:
        value = "CAPPED AND PERCENTAGE";
        break;
      case VoucherTypeEnum.SHIPPING:
        value = "FREE SHIPPING";
        break;
      case VoucherTypeEnum.E_VOUCHER:
        value = "E-VOUCHER";
        break;
      case "FIXED":
        value = "FIXED AMOUNT";
        break;
      default:
        value = "PERCENTAGE";
        break;
    }
    return value;
  }
  return (
    <ResponsiveTable>
      <TableHead
        colSpan={tableHeadNumCol}
        selected={selected}
        disabled={disabled}
        items={voucherGroups}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.code
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(VoucherListUrlSortField.code)}
          className={classes.colName}
        >
          <FormattedMessage
            defaultMessage="Voucher Code"
            description="voucher code"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="left" className={classes.colType}>
          <FormattedMessage
            defaultMessage="Discount Type"
            description="voucher type"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.minSpent
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.minSpent)}
          className={classes.colMinSpent}
        >
          <FormattedMessage
            defaultMessage="Minimum Spent"
            description="minimum amount of spent money to activate voucher"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.startDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.startDate)}
          className={classes.colStart}
        >
          <FormattedMessage
            defaultMessage="Start Date"
            description="voucher is active from date"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.endDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.endDate)}
          className={classes.colEnd}
        >
          <FormattedMessage
            defaultMessage="End Date"
            description="voucher is active until date"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === VoucherListUrlSortField.value
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="right"
          onClick={() => onSort(VoucherListUrlSortField.value)}
          className={classes.colValue}
        >
          <FormattedMessage
            defaultMessage="Discount Value"
            description="voucher value"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="center" className={classes.colUses}>
          <FormattedMessage
            defaultMessage="Voucher Status"
            description="voucher status"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="left" className={classes.emptyHeader} />
        <TableCellHeader textAlign="left" className={classes.emptyHeader} />
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={getFooterColSpanWithBulkActions(
              voucherGroups,
              numberOfColumns
            )}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          voucherGroups,
          (voucherGroup, index) => {
            const isSelected = voucherGroup
              ? isChecked(voucherGroup.id)
              : false;
            const voucherGroupNode = voucherGroup?.vouchers?.edges?.[0]?.node;

            return (
              <TableRow
                className={!!voucherGroup ? classes.tableRow : undefined}
                hover={!!voucherGroup}
                key={voucherGroup ? voucherGroup.id : "skeleton"}
                selected={isSelected}
                // onClick={voucherGroup ? onRowClick(voucherGroup.id) : undefined}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(voucherGroup.id)}
                    id={`groupVoucherCheckboxRow${index}`}
                  />
                </TableCell>
                <TableCell
                  className={classes.colName}
                  id={`groupVoucherCodeRow${index}`}
                >
                  <div className={classes.tableCodes}>
                    {maybe<React.ReactNode>(
                      () => voucherGroup?.code,
                      <Skeleton />
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className={classes.colType}
                  id={`groupVoucherDiscountTypeRow${index}`}
                >
                  {maybe<React.ReactNode>(
                    () => getVoucherType(voucherGroup),
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colMinSpent}
                  id={`groupVoucherMinSpentRow${index}`}
                >
                  {voucherGroup && voucherGroupNode?.minSpent ? (
                    <Money money={voucherGroupNode?.minSpent} />
                  ) : voucherGroup && voucherGroupNode?.minSpent === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colStart}
                  id={`groupVoucherStartDateRow${index}`}
                >
                  {voucherGroup && voucherGroupNode?.startDate ? (
                    <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                      {voucherGroupNode?.startDate}
                    </Moment>
                  ) : (
                    // <Date date={voucher.startDate} />
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colEnd}
                  id={`groupVoucherEndDateRow${index}`}
                >
                  {voucherGroup && voucherGroupNode?.endDate ? (
                    <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                      {voucherGroupNode?.endDate}
                    </Moment>
                  ) : // <Date date={voucher.endDate} />
                  voucherGroup && voucherGroupNode?.endDate === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colValue}
                  id={`groupVoucherDiscountValueRow${index}`}
                >
                  {voucherGroup &&
                  voucherGroupNode?.discountValueType &&
                  voucherGroupNode?.discountValueType ? (
                    voucherGroupNode?.discountValueType ===
                    DiscountValueTypeEnum.FIXED ? (
                      <Money
                        money={{
                          amount: voucherGroupNode?.discountValue,
                          currency: defaultCurrency
                        }}
                      />
                    ) : (
                      <Percent amount={voucherGroupNode?.discountValue} />
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colUses}
                  onClick={e => e.stopPropagation()}
                  id={`groupVoucherVoucherStatusRow${index}`}
                >
                  <div>
                    {maybe<React.ReactNode>(
                      () => voucherGroupNode?.status,
                      <Skeleton />
                    )}
                  </div>
                </TableCell>
                <TableCell className={classes.colIcons}>
                  <Button
                    className={classes.btnList}
                    id={`groupVoucherBtnRow${index}`}
                    onClick={
                      !!voucherGroup
                        ? onArrowClick(voucherGroup?.id)
                        : undefined
                    }
                  >
                    <ListIcon className={classes.list} />
                  </Button>
                </TableCell>
                <TableCell className={classes.colIcons}>
                  <IconButton
                    onClick={() => onBulkGroupListDownload(voucherGroup?.id)}
                  >
                    <img src={DownloadIcon} style={{ width: "20px" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No vouchers found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
VoucherGroupList.displayName = "VoucherGroupList";
export default VoucherGroupList;
